import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderDialogService,
  ElderHeaderComponent,
  ElderMultiSelectChipsComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderThemeDirective,
  ElderToastService,
  ElderValidationErrorDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../features/validators/id-validator'
import { MaterialMatcher } from '../../../matcher/material-matcher'
import { MaterialMatcherDetailComponent } from '../../../matcher/material-matcher-detail/material-matcher-detail.component'
import { SerialNumberSpec } from '../../serial-number-spec'
import { VendorMultiSelectDirective } from '../../vendors/vendor-multi-select.directive'
import { BatchTracingSpec } from '../batch-tracing-spec'
import { BatchTracingSpecService } from '../batch-tracing-spec.service'

@Component({
  selector: 'app-batch-tracing-spec-detail',
  templateUrl: './batch-tracing-spec-detail.component.html',
  styleUrls: ['./batch-tracing-spec-detail.component.scss'],
  imports: [
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneTitleComponent,
    ElderPaneComponent,
    ElderBasicPaneLayoutComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ElderValidationErrorDirective,
    MaterialMatcherDetailComponent,
    ElderMultiSelectChipsComponent,
    VendorMultiSelectDirective,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class BatchTracingSpecDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _spec$: BehaviorSubject<BatchTracingSpec> = new BehaviorSubject(null)

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern

  private route = inject(ActivatedRoute)
  private batchTracingSpecService = inject(BatchTracingSpecService)
  private toastService = inject(ElderToastService)
  private dialogService = inject(ElderDialogService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.spec = this.route.snapshot.data['spec']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.spec) {
      this.spec = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get spec$(): Observable<BatchTracingSpec> {
    return this._spec$.asObservable()
  }

  public get spec(): BatchTracingSpec {
    return this._spec$.getValue()
  }

  @Input()
  public set spec(spec: BatchTracingSpec) {
    if (spec) {
      this._creationMode = !spec.id

      this._spec$.next(spec)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.spec.id) {
      this.batchTracingSpecService.findById(this.spec.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.batchTracingSpecService.update(this.spec).subscribe({
      next: (updated) => {
        this.spec = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'messages.deleting_confirmation_msg',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.batchTracingSpecService.delete(this.spec).subscribe({
            next: (_) => this.close.emit(),
            error: (err) => this.toastService.error('messages.deleting_failed', err),
          })
        }
      })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): BatchTracingSpec {
    return new BatchTracingSpec('Neu', <SerialNumberSpec>{}, [], new MaterialMatcher())
  }
}
